import {
  pgTable,
  uuid,
  text,
  boolean,
  numeric,
  jsonb,
  timestamp,
  integer,
  index,
} from "drizzle-orm/pg-core";
import { customers } from "./customers.js";
import { customerGroups } from "./customer-groups.js";
import { priceLists } from "./price-lists.js";
import { inventoryLocations } from "./inventory-locations.js";

export const orders = pgTable(
  "orders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orderNumber: text("order_number").notNull().unique(),
    customerId: uuid("customer_id").references(() => customers.id, {
      onDelete: "set null",
    }),
    customerEmail: text("customer_email").notNull(),
    customerFirstName: text("customer_first_name"),
    customerLastName: text("customer_last_name"),
    customerPhone: text("customer_phone"),
    priceListId: uuid("price_list_id").references(() => priceLists.id),
    customerGroupId: uuid("customer_group_id").references(() => customerGroups.id),
    status: text("status")
      .notNull()
      .default("pending")
      .$type<"pending" | "paid" | "ready" | "fulfilled" | "cancelled" | "refunded">(),
    paymentStatus: text("payment_status")
      .notNull()
      .default("pending")
      .$type<"pending" | "paid" | "partially_refunded" | "refunded" | "failed">(),
    subtotal: numeric("subtotal", { precision: 12, scale: 2 }).notNull().default("0"),
    shippingCost: numeric("shipping_cost", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    depositTotal: numeric("deposit_total", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    couponDiscount: numeric("coupon_discount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    totalAmount: numeric("total_amount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    currency: text("currency").notNull().default("EUR"),
    appliedCouponId: uuid("applied_coupon_id"),
    vatDisplay: text("vat_display").notNull().default("inc").$type<"inc" | "exc">(),
    fulfilmentType: text("fulfilment_type")
      .notNull()
      .default("pickup")
      .$type<"pickup" | "courier">(),
    pickupLocationId: uuid("pickup_location_id").references(() => inventoryLocations.id),
    // Store that actually fulfils the order. Always populated post-
    // 20260615120000: pickup orders → customer's chosen store, courier
    // orders → org default. Admin can reassign at any time.
    fulfilmentStoreId: uuid("fulfilment_store_id").references(() => inventoryLocations.id),
    courierProvider: text("courier_provider"),
    shippingAddress: jsonb("shipping_address"),
    pickupPointTerminal: jsonb("pickup_point_terminal"),
    // Venipak-specific state. pack_numbers is a Postgres text[] (one
    // shipment may produce multiple labels); manifest_code groups them
    // for the printable consolidated PDF. pickup_point_id is the
    // integer Venipak parcel-locker id from their /ws/get_pickup_points
    // feed. status mirrors what the admin sees ("sent" / "error").
    venipakPackNumbers: text("venipak_pack_numbers").array(),
    venipakManifestCode: text("venipak_manifest_code"),
    venipakPickupPointId: integer("venipak_pickup_point_id"),
    venipakStatus: text("venipak_status"),
    venipakDispatchedAt: timestamp("venipak_dispatched_at", { withTimezone: true }),
    // First-party session attribution — set on storefront checkout so
    // the /performance/visitors funnel can join orders back to the
    // originating session. NULL for legacy / admin-created orders.
    sessionId: uuid("session_id"),
    storefrontSource: text("storefront_source").notNull().default("lv"),
    receiveChilled: boolean("receive_chilled").notNull().default(false),
    forBanquet: boolean("for_banquet").notNull().default(false),
    // Earliest legally-deliverable instant for courier orders containing
    // alcohol (LV: order + 6h, before the 20:00 / 17:00-Sun cutoff). NULL for
    // pickup, parcel-locker, and alcohol-free orders. Stamped at order
    // creation — see apps/api/src/store/delivery-window.ts.
    earliestDeliveryAt: timestamp("earliest_delivery_at", { withTimezone: true }),
    trackingNumber: text("tracking_number"),
    paymentMethod: text("payment_method"),
    // Human display label for HOW the order was paid ("Swedbank", "Karte",
    // "Pārskaitījums") — stamped at checkout, refined by the Montonio
    // webhook's paymentProviderName. paymentMethod keeps its coarse
    // machine value ("bank"/"card"/"bank_transfer") for filters/logic.
    paymentMethodDetail: text("payment_method_detail"),
    isB2bInvoice: boolean("is_b2b_invoice").notNull().default(false),
    // B2B requisites snapshot from checkout ("Juridiska persona"):
    // { buyerType, companyName, regNumber, vatNumber, legalAddress }.
    billingDetails: jsonb("billing_details").$type<{
      buyerType: "private" | "business";
      companyName?: string;
      regNumber?: string;
      vatNumber?: string;
      legalAddress?: string;
    } | null>(),
    // Per-item gift packaging selection: [{ productId, sku, name, qty }].
    // Cost charged at gift_wrap_price_cents (store_settings) per unit.
    giftWrap: jsonb("gift_wrap").$type<
      Array<{ productId: string; sku: string | null; name: string; qty: number }> | null
    >(),
    giftWrapCost: numeric("gift_wrap_cost", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    ageVerificationId: uuid("age_verification_id"),
    customerNote: text("customer_note"),
    internalNote: text("internal_note"),
    // Set to hide an order from the panelis lists without deleting it
    // (owner 2026-10-02 — testing-period cleanup). Distinct from
    // "cancelled", which is a real business state staff still review.
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    paidAt: timestamp("paid_at", { withTimezone: true }),
    fulfilledAt: timestamp("fulfilled_at", { withTimezone: true }),
    cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
  },
  (t) => ({
    customerIdx: index("orders_customer_id_idx").on(t.customerId),
    statusIdx: index("orders_status_idx").on(t.status),
    paymentStatusIdx: index("orders_payment_status_idx").on(t.paymentStatus),
    createdAtIdx: index("orders_created_at_idx").on(t.createdAt),
    fulfilmentStoreIdx: index("orders_fulfilment_store_id_idx").on(t.fulfilmentStoreId),
  })
);

export type Order = typeof orders.$inferSelect;
export type NewOrder = typeof orders.$inferInsert;

export const ORDER_STATUSES = [
  "pending",
  "paid",
  "ready",
  "fulfilled",
  "cancelled",
  "refunded",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = [
  "pending",
  "paid",
  "partially_refunded",
  "refunded",
  "failed",
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
